import { LOTTERY_TYPES } from "../../../config/lotteryConfig";
import { formatAmount, formatCount, formatDate, formatRound } from "../../../utils/common/format";
import LotteryNumlotWinningNumbers from "../LotteryNumlotWinningNumbers";
import AdRectangle from "../../ads/AdRectangle";
import PageMeta from "../../common/PageMeta";
import NoData from "../../common/NoData";
import LotteryNumlotDetailNearTemplate from "./LotteryNumlotDetailNearTemplate";
import { useEffect, useRef, useState } from "react";

const MIN_LOADING_MS = 500;

// スコアを星・色に変換
const SCORE_CONFIG = {
  1: { label: "★☆☆☆☆", color: "text-primary-500" },
  2: { label: "★★☆☆☆", color: "text-primary-500" },
  3: { label: "★★★☆☆", color: "text-primary-500" },
  4: { label: "★★★★☆", color: "text-primary-500" },
  5: { label: "★★★★★", color: "text-primary-500" },
};

export default function LotteryNumlotDetailTemplate({
  data,
  config,
  loading,
  aiAnalysis,
  related,
}) {
  const [showLoading, setShowLoading] = useState(loading);
  const loadingStartedAt = useRef(Date.now());
  const totalPrize = data?.payouts?.reduce((sum, p) => sum + ((p.amount ?? 0) * (p.winners ?? 0)), 0) ?? null;
  const totalWinners = data?.payouts?.reduce((sum, p) => sum + (p.winners ?? 0), 0) ?? null;
  const salesCount = data?.sales_amount && config?.price ? Math.floor(data.sales_amount / config.price) : null;

  useEffect(() => {
    let timer;
    if (loading) {
      loadingStartedAt.current = Date.now();
      setShowLoading(true);
    } else {
      const elapsed = Date.now() - loadingStartedAt.current;
      const remaining = Math.max(0, MIN_LOADING_MS - elapsed);
      timer = setTimeout(() => setShowLoading(false), remaining);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  if (showLoading) {
    return (
      <div className="container max-w-[768px] mx-auto">
        <div className="py-4 space-y-2 animate-pulse">
          <div className="h-4 bg-primary-500/30 rounded w-3/4" />
          <div className="h-4 bg-primary-500/20 rounded w-1/2" />
          <div className="h-4 bg-primary-500/10 rounded w-2/3" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <>
        <PageMeta title="データが見つかりません" description="該当するデータが見つかりませんでした。" noindex />
        <NoData />
      </>
    );
  }

  const scoreConf = aiAnalysis?.score ? SCORE_CONFIG[aiAnalysis.score] : null;

  // 払戻金・当選口数・販売実績のいずれも記録がない回（主に2008年頃以前）
  const hasPayoutData =
    (data.payouts || []).some((p) => p.amount != null || p.winners != null) ||
    data.sales_amount != null ||
    (data.carryover != null && data.carryover > 0);

  return (
    <>
      <PageMeta
        title={`${formatRound(data.round)} | ${config?.label}`}
        description={`${config?.label} ${formatRound(data.round)}（${formatDate(data.draw_date)}）の当選番号・払戻金情報。`}
        path={`${config?.path}detail/${data.round}`}
        breadcrumbs={[
          { name: "TOP", path: "/" },
          { name: config?.label, path: config?.path },
          { name: formatRound(data.round), path: `${config?.path}detail/${data.round}` },
        ]}
      />
      <div className="container mx-auto p-4 md:p-8 bg-base-50 rounded-xl">
        <div className="flex justify-between items-center flex-wrap mb-16 border-b border-b-2 border-primary-500 pb-2">
          <h1 className="text-2xl font-bold text-primary-500">
            {config?.label} {formatRound(data.round)}
          </h1>
          <div className="text-primary-500 font-bold ml-auto">
            <dl className="grid grid-cols-[auto_auto] gap-x-2 justify-end text-sm">
              <dt>抽選日</dt>
              <dd>{formatDate(data.draw_date)}</dd>
            </dl>
          </div>
        </div>

        {/* 当選番号 */}
        <div className="mb-16 text-center">
          <LotteryNumlotWinningNumbers
            mainNumber={data.main_number}
            bonusNumber={data.bonus_number}
            config={config}
            size="lg"
          />
        </div>

        {/* 払戻テーブル */}
        <div className="relative">
        <div className={`overflow-x-auto ${!hasPayoutData ? "opacity-40 select-none pointer-events-none" : ""}`}>
          <table className="w-full text-[10px] sm:text-xs md:text-sm border border-primary-500 whitespace-nowrap">
            <thead>
              <tr className="text-primary-500 border-b border-primary-500 text-left"
                style={{
                  backgroundColor: '#fdfbf7',
                  backgroundImage: 'radial-gradient(circle, rgba(227,0,127,0.2) 1px, transparent 1px), radial-gradient(circle, rgba(227,0,127,0.2) 1px, transparent 1px)',
                  backgroundPosition: '2px 0, 6px 2px',
                  backgroundSize: '8px 4px',
                }}
              >
                <th className="px-2 md:px-4 py-2 font-bold">［ 等級 ］</th>
                <th className="px-2 md:px-4 py-2 font-bold text-right">［ 当選口数 ］</th>
                <th className="px-2 md:px-4 py-2 font-bold text-right">［ 払戻金 ］</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dashed divide-primary-500">
              {(data.payouts || []).map((payout, i) => (
                <tr key={i}>
                  <td className="px-2 md:px-4 py-2 text-primary-500">
                    {payout.name ? payout.name : `［ ${payout.grade}等 ］`}
                  </td>
                  <td className="px-2 md:px-4 py-2 text-right font-bold text-primary-500">{formatCount(payout.winners)}</td>
                  <td className="px-2 md:px-4 py-2 text-right font-bold text-primary-500">{formatAmount(payout.amount)}</td>
                </tr>
              ))}
              {data.carryover != null && data.carryover > 0 && (
                <tr>
                  <td className="px-2 md:px-4 py-2 text-primary-500">［ キャリーオーバー ］</td>
                  <td colSpan={2} className="px-2 md:px-4 py-2 text-right font-bold text-primary-500">
                    {formatAmount(data.carryover)}
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="text-primary-500 border-y border-primary-500 text-left"
                style={{
                  backgroundColor: '#fdfbf7',
                  backgroundImage: 'radial-gradient(circle, rgba(227,0,127,0.2) 1px, transparent 1px), radial-gradient(circle, rgba(227,0,127,0.2) 1px, transparent 1px)',
                  backgroundPosition: '2px 0, 6px 2px',
                  backgroundSize: '8px 4px',
                }}
              >
                <th className="px-2 md:px-4 py-2 font-bold">［ 小計 ］</th>
                <th className="px-2 md:px-4 py-2 font-bold text-right">［ 総数 ］</th>
                <th className="px-2 md:px-4 py-2 font-bold text-right">［ 総額 ］</th>
              </tr>
              {totalPrize != null && totalPrize > 0 && (
                <tr className="border-t border-primary-500">
                  <td className="px-2 md:px-4 py-2 text-primary-500">［ 総合払戻 ］</td>
                  <td className="px-2 md:px-4 py-2 text-right font-bold text-primary-500">
                    {totalWinners != null ? `${totalWinners.toLocaleString()}口` : ''}
                  </td>
                  <td className="px-2 md:px-4 py-2 text-right font-bold text-primary-500">{formatAmount(totalPrize)}</td>
                </tr>
              )}
              {data.sales_amount != null && (
                <tr className="border-t border-primary-500">
                  <td className="px-2 md:px-4 py-2 text-primary-500">［ 販売実績 ］</td>
                  <td className="px-2 md:px-4 py-2 text-right font-bold text-primary-500">
                    {salesCount != null ? `${salesCount.toLocaleString()}枚` : ''}
                  </td>
                  <td className="px-2 md:px-4 py-2 text-right font-bold text-primary-500">{formatAmount(data.sales_amount)}</td>
                </tr>
              )}
            </tfoot>
          </table>
          <p className="mt-2 text-[10px] text-primary-500/50 text-right">
            ※データの正確性は保証しません。必ず公式サイトでご確認ください。
          </p>
        </div>
        {!hasPayoutData && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="bg-base-50 border border-primary-500 text-primary-500 text-xs md:text-sm font-bold px-5 py-2.5 rounded shadow-md">
              詳細データがありません
            </span>
          </div>
        )}
        </div>

        {/* AI解析セクション */}
        {aiAnalysis && (
          <>
            <div className="mt-16 border border-primary-500 overflow-hidden">
              <div className="px-4 py-3 border-b border-primary-500 flex items-center justify-between"
                style={{
                  backgroundColor: '#fdfbf7',
                  backgroundImage: 'radial-gradient(circle, rgba(227,0,127,0.2) 1px, transparent 1px), radial-gradient(circle, rgba(227,0,127,0.2) 1px, transparent 1px)',
                  backgroundPosition: '2px 0, 6px 2px',
                  backgroundSize: '8px 4px',
                }}
              >
                <h2 className="text-[10px] sm:text-xs md:text-sm font-bold text-primary-500">［ AI解析 ］</h2>
                  {scoreConf && (
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] sm:text-xs md:text-sm font-bold ${scoreConf.color}`}>1等払戻金評価</span>
                      <span className={`text-[10px] sm:text-xs md:text-sm font-bold ${scoreConf.color}`}>{scoreConf.label}</span>
                    </div>
                  )}
              </div>

              <div className="p-4 space-y-4">
                {/* サマリー */}
                {aiAnalysis.summary && (
                  <p className="text-[10px] sm:text-xs md:text-sm text-primary-500 leading-relaxed">{aiAnalysis.summary}</p>
                )}

                {/* 払戻金分析 */}
                {aiAnalysis.prize_analysis && (
                  <div className="text-[10px] sm:text-xs md:text-sm text-primary-500 space-y-1">
                    <p>{aiAnalysis.prize_analysis.comment}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-primary-500">
                      {aiAnalysis.prize_analysis.vs_avg && <span>平均比：{aiAnalysis.prize_analysis.vs_avg}</span>}
                      {aiAnalysis.prize_analysis.vs_median && <span>中央値比：{aiAnalysis.prize_analysis.vs_median}</span>}
                    </div>
                  </div>
                )}

                {/* 数字分析 */}
                {aiAnalysis.number_analysis && (
                  <div className="text-[10px] sm:text-xs md:text-sm  text-primary-500">
                    <span className="inline-block px-2 py-0.5 border border-primary-500 rounded text-[10px] mr-2">
                      {aiAnalysis.number_analysis.pattern}
                    </span>
                    {aiAnalysis.number_analysis.comment}
                  </div>
                )}

                {/* 傾向比較 */}
                {aiAnalysis.trend_comparison && (
                  <p className="text-[10px] sm:text-xs md:text-sm text-primary-500">{aiAnalysis.trend_comparison}</p>
                )}

                {/* 類似回 */}
                {aiAnalysis.similar_past && aiAnalysis.similar_past.length > 0 && (
                  <div className="text-[10px] sm:text-xs md:text-sm text-primary-500">
                    <p className="font-bold mb-1 text-primary-500">類似した過去の回：</p>
                    <ul className="space-y-1">
                      {aiAnalysis.similar_past.map((s, i) => (
                        s.round > 0 && (
                          <li key={i}>
                            <a
                              href={`${config?.path}detail/${s.round}`}
                              className="underline hover:opacity-70"
                            >
                              第{s.round}回
                            </a>
                            {s.comment && <span className="ml-2">{s.comment}</span>}
                          </li>
                        )
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* フッター */}
              <div className="px-4 py-2 border-t border-dashed border-primary-500 flex items-center justify-end">
                <p className="text-[10px] text-primary-500">
                  Powered by {aiAnalysis.ai_model || "Gemini"}
                </p>
              </div>
            </div>
            <p className="mt-2 text-[10px] text-primary-500/50 text-right">
              ※AI解析は参考情報です。当選を保証するものではありません。
            </p>
          </>
        )}

        {/* 近い過去回 */}
        <LotteryNumlotDetailNearTemplate related={related} config={config} />

        {/* ページネーション（SP:2列＋TOP全幅／PC:3等分。リサイズで動かないようgrid固定） */}
        <div className="mt-16 grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 text-xs sm:text-sm">
          <button
            type="button"
            disabled={Number(data.round) <= 1}
            onClick={() => { window.location.href = `${config?.path}detail/${Number(data.round) - 1}`; }}
            className="sm:order-1 px-2 sm:px-4 py-2 text-center whitespace-nowrap border border-primary-500 rounded-lg text-primary-500 hover:bg-primary-500 hover:text-base-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ‹ 第{Number(data.round) - 1}回
          </button>
          <button
            type="button"
            disabled={data.is_latest}
            onClick={() => { window.location.href = `${config?.path}detail/${Number(data.round) + 1}`; }}
            className="sm:order-3 px-2 sm:px-4 py-2 text-center whitespace-nowrap border border-primary-500 rounded-lg text-primary-500 hover:bg-primary-500 hover:text-base-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            第{Number(data.round) + 1}回 ›
          </button>
          <button
            type="button"
            onClick={() => { const path = LOTTERY_TYPES[data.lotteryKey]?.path || "/"; window.location.href = path; }}
            className="col-span-2 sm:col-span-1 sm:order-2 px-2 sm:px-4 py-2 text-center whitespace-nowrap bg-primary-500 text-base-50 border border-primary-500 rounded-lg hover:bg-base-50 hover:text-primary-500 transition-colors"
          >
            {config?.label} TOP
          </button>
        </div>
      </div>
      <div className="container mx-auto mt-8 mb-8 flex justify-center flex-wrap gap-4">
        <AdRectangle />
        <AdRectangle />
      </div>
    </>
  );
}
